<template>
  <div
    v-if="newCustomer"
    class="has-padding-bottom"
  >
    <Message
      class="is-danger has-margin-bottom"
      :message="
        $t('Components.Customers.CreateCustomer.Message_EmailAlreadyExists')
      "
      v-if="emailAlreadyExists"
    />
    <BaseInputField
      v-model="newCustomer.FirstName"
      :label="$t(`Components.Customers.CreateCustomer.Label_FirstName`)"
      :labelForInput="labelForInput"
      :error="firstNameErrors.toString()"
      @input="delayTouch($v.newCustomer.FirstName)"
      @blur="$v.newCustomer.FirstName.$touch()"
      required
    />
    <BaseInputField
      v-model="newCustomer.LastName"
      :label="$t(`Components.Customers.CreateCustomer.Label_LastName`)"
      :labelForInput="labelForInput"
      :error="lastNameErrors.toString()"
      @input="$v.newCustomer.LastName.$touch()"
      @blur="$v.newCustomer.LastName.$touch()"
      required
    />

    <BaseInputField
      v-model="newCustomer.Email"
      :label="$t(`Components.Customers.CreateCustomer.Label_Email`)"
      :labelForInput="labelForInput"
      :error="emailErrors.toString()"
      @change="delay(checkCustomerEmail, 200)"
      @input="$v.newCustomer.Email.$touch()"
      @blur="$v.newCustomer.Email.$touch()"
      required
    />

    <BaseInputField
      v-model="newCustomer.Phone"
      :label="$t(`Components.Customers.CreateCustomer.Label_Phone`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />

    <hr />

    <BaseInputField
      v-model="newCustomer.Address"
      :label="$t(`Components.Customers.CreateCustomer.Label_Address`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />

    <BaseInputField
      v-model="newCustomer.PostalCode"
      :label="$t(`Components.Customers.CreateCustomer.Label_Postalcode`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />
    <BaseInputField
      v-model="newCustomer.City"
      :label="$t(`Components.Customers.CreateCustomer.Label_City`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />
    <BaseInputField
      v-model="newCustomer.State"
      :label="$t(`Components.Customers.CreateCustomer.Label_State`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />
    <BaseInputField
      v-model="newCustomer.Country"
      :label="$t(`Components.Customers.CreateCustomer.Label_Country`)"
      :labelForInput="labelForInput"
      error=""
      :required="false"
    />

    <div
      v-if="showSaveButton"
      class="level-right"
    >
      <button
        class="button is-primary"
        @click="createCustomer"
        :class="{ 'is-loading': isSaving || isCheckingEmail }"
        :disabled="Boolean($v.$error) || isCheckingEmail || emailAlreadyExists"
        v-html="$t(`Components.Customers.CreateCustomer.Button_CreateCustomer`)"
      ></button>
    </div>
  </div>
</template>

<script>
import customerProvider from '@/providers/customer'
import { required, email, minLength } from 'vuelidate/lib/validators'
import { EventBus } from '@/eventbus/event-bus'
const touchMap = new WeakMap()

export default {
  name: 'createCustomer',
  components: {
    BaseInputField: () => import('@/components/UI/Form/BaseInputField'),
    Message: () => import('@/components/UI/Message'),
  },

  props: {
    locationId: {
      type: Number,
      required: true,
    },

    labelForInput: {
      type: Boolean,
      default: true,
    },

    showSaveButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isCheckingEmail: false,
      isSaving: false,
      emailAlreadyExists: false,
      newCustomer: {
        Id: 0,
        LocationId: this.locationId,
        Gender: '',
        FirstName: '',
        LastName: '',
        Email: '',
        Phone: '',
        Address: '',
        PostalCode: '',
        City: '',
        State: '',
        Country: '',
        BirtdahDay: 0,
        BirthdayMonth: 0,
        BirthdayYear: 0,
      },
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      FirstName: {
        required,
        minLength: minLength(2),
      },
      LastName: {
        required,
        minLength: minLength(2),
      },
      Email: {
        required,
        email,
      },
    }

    return {
      newCustomer: output,
    }
  },

  computed: {
    firstNameErrors() {
      const errors = []
      if (!this.$v.newCustomer.FirstName.$dirty) return errors
      !this.$v.newCustomer.FirstName.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.newCustomer.FirstName.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },
    lastNameErrors() {
      const errors = []
      if (!this.$v.newCustomer.LastName.$dirty) return errors
      !this.$v.newCustomer.LastName.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      return errors
    },
    emailErrors() {
      const errors = []
      if (!this.$v.newCustomer.Email.$dirty) return errors
      if (!this.$v.newCustomer.Email.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      } else if (!this.$v.newCustomer.Email.email) {
        errors.push(this.$t('Form.InputErrors.InvalidEmail'))
      } else if (this.emailAlreadyExists) {
        errors.push(this.$t('Form.InputErrors.EmailExists'))
      }
      return errors
    },
  },

  created() { },

  methods: {
    delayTouch($v) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, 1000))
    },

    checkCustomerEmail() {
      customerProvider.methods
        .checkIfCustomerExists(this.locationId, this.newCustomer.Email)
        .then((response) => {
          if (response.status === 200) {
            this.emailAlreadyExists = response.data
          }
        })
        .catch((err) => { })
        .finally(() => { })
    },

    delay(callback, ms) {
      let self = this

      if (self.timer) {
        clearTimeout(self.timer)
      }
      this.timer = setTimeout(() => {
        callback.apply(self)
        clearTimeout(self.timer)
      }, ms || 0)
    },

    async createCustomer() {
      let self = this
      this.errorMessage = ''
      //this.emailAlreadyExists = false
      if (!this.isSaving && !this.emailAlreadyExists) {
        this.isSaving = true
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.isSaving = false
          return false
        } else {
          await customerProvider.methods
            .createCustomer(this.newCustomer)
            .then((response) => {
              if (response.status === 200) {
                self.$emit('nextAction', response.data)
                EventBus.$emit('customerCreated', response.data)

                EventBus.$emit('showToast', {
                  type: 'info',
                  message: self.$t(
                    `Components.Customers.CreateCustomer.Toast_CustomerCreated`,
                    {
                      customer: response.data.FullName,
                    }
                  ),
                })
              } else {
                self.errorMessage = self.$t(
                  'Components.AccountControls.Register.Message_ErrorCode501'
                )
              }
            })
            .catch((e) => {
              if (e.response && e.response.status === 403) {
                this.emailAlreadyExists = true
              }
              self.errorMessage = self.$t(
                'Components.AccountControls.Register.Message_ErrorCode500'
              )
              self.isSaving = false
            })

          return true
        }
      }
    },
  },
}
</script>
